// OwnerTopBar.tsx — الشريط العلويّ لبوابة الملّاك (Owner_Portal_UX_Redesign_Plan_2026-07-12.md
// §3.1، توزيع v2). يستبدل OwnerSidebar.tsx المحذوف: بلا قائمة جانبيّة إطلاقًا، 6 تبويبات حبوب
// (pills) ممركزة (تشمل «إحصائياتي» بجانب «خيولي» بطلب المستخدم) + شعار النظام القابل للتخصيص
// وقت التشغيل (نفس نمط جلب /system-name المستخدَم في LoginPage.tsx). مُبدِّل الإدارة/المالك
// (PortalSwitcher) أُزيل بالكامل بطلب المستخدم (2026-07-12): جمهور البوابة ملّاك لا يعرفون
// بوجود واجهة إدارة أصلًا — الحسابات المزدوجة النادرة تعود لواجهة الإدارة عبر الرابط مباشرة.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { hasPagePermission } from '../../lib/accessControl';
import { readJsonStorage } from '../../lib/storage';
import type { Permission } from '../../lib/accessControl';
import { OWNER_NAV_ITEMS } from './ownerNav';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { UserMenu } from '../UserMenu';
import { SimpleTooltip } from '../../components/ui/simple-tooltip';
import { cn } from '../../lib/utils';

interface BrandInfo {
	name: string;
	logo: string | null;
}

export function OwnerTopBar() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [brand, setBrand] = useState<BrandInfo>({ name: '', logo: null });

	useEffect(() => {
		api
			.get<{ system_name?: string; logo?: string }>('/system-name')
			.then(({ data }) => setBrand({ name: data?.system_name || '', logo: data?.logo || null }))
			.catch(() => undefined);
	}, []);

	const permissions = readJsonStorage<Permission[]>('permissions', []);
	const tabs = OWNER_NAV_ITEMS.filter((item) => hasPagePermission(permissions, [], item.path));

	const pathname = location.pathname.toLowerCase();

	return (
		<header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-white/10 bg-owner-sidebar px-3 text-owner-sidebar-foreground shadow-sm sm:px-5">
			<div className="flex shrink-0 items-center gap-2.5">
				{brand.logo ? (
					<img src={brand.logo} alt="logo" className="h-8 max-w-[96px] object-contain" />
				) : (
					<div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-owner-accent font-black text-white ring-1 ring-white/20">SS</div>
				)}
				<span className="hidden truncate text-[15px] font-bold sm:inline">{t('ownerShell.portalTitle', 'بوابة الملّاك')}</span>
			</div>

			<nav className="hidden flex-1 items-center justify-center gap-1 sm:flex">
				{tabs.map((item) => {
					const active = pathname === item.path.toLowerCase() || pathname.startsWith(`${item.path.toLowerCase()}/`);
					const label = t(item.textKey, item.fallback);
					const button = (
						<button
							key={item.path}
							onClick={() => navigate(item.path)}
							aria-current={active ? 'page' : undefined}
							className={cn(
								'flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] transition-colors md:px-3.5',
								active ? 'bg-white/12 font-bold text-white' : 'text-owner-sidebar-foreground/70 hover:bg-white/5 hover:text-white',
							)}
						>
							<item.Icon size={19} strokeWidth={active ? 2 : 1.5} color={active ? 'var(--owner-accent)' : undefined} />
							<span className="hidden md:inline">{label}</span>
						</button>
					);
					return (
						<SimpleTooltip key={item.path} label={label}>
							{button}
						</SimpleTooltip>
					);
				})}
			</nav>

			<div className="ms-auto flex flex-1 items-center justify-end gap-1 sm:flex-none">
				<LanguageSwitcher />
				<UserMenu />
			</div>
		</header>
	);
}
